/**
 * AIOps 대시보드 공용 상수(다수 컴포넌트 참조) — 공용 텍스트/색상 매핑·정렬 순서 집약.
 * 도메인 타입은 dashboard.types.ts, 단일 컴포넌트 전용 상수는 각 *.define.ts.
 */
import type { Severity } from '@/aiops/utils/dashboard.types';
import { scoreToSeverity } from '@/aiops/utils/scoring.utils';
import { i18n } from '@/common/locale';

/**
 * Severity 노출 순서 — 위험도 높은 순, 상태 미확정(`unknown`)은 맨 뒤.
 * 헤더 필터 pill 순서·초기 전체 선택 모수로 함께 쓰인다.
 */
export const ALL_SEVERITIES: readonly Severity[] = [
  'critical',
  'warning',
  'normal',
  'unknown',
] as const;

/** 데이터 미수신(score = null) 시 표기 문자. 숫자 자리에 '-'. */
export const EMPTY_VALUE_TEXT = '-';

/** 상태 미확정 색상 — 상태색(초록/노랑/빨강) 대신 중립 회색. */
export const EMPTY_COLOR_VAR = 'var(--color-gray-02-10)';

/** Severity → 라벨. BizSummaryBar의 STATUS_TEXT, BizGroupCard의 BADGE_TEXT 와 동일 */
export const SEVERITY_TEXT: Record<Severity, string> = {
  critical: i18n.global.t('WORD.CRITICAL_STATUS'),
  warning: i18n.global.t('WORD.WARNING'),
  normal: i18n.global.t('WORD.NORMAL_STATUS'),
  unknown: i18n.global.t('WORD.UNKNOWN'),
};

/**
 * Severity → CSS 변수 색상 문자열. SVG attribute 에 var() 는 직접 미작동 —
 * :style="{ stroke: SEVERITY_COLOR_VAR[...] }" 인라인 CSS 로 적용(클래스는 --color-{green|yellow|red}-05-05 직접 참조).
 */
export const SEVERITY_COLOR_VAR: Record<Severity, string> = {
  critical: 'var(--color-red-05-05)',
  warning: 'var(--color-yellow-05-05)',
  normal: 'var(--color-green-05-05)',
  unknown: EMPTY_COLOR_VAR,
};

/** severity → 색상. `unknown`(상태 미확정) 이면 회색. */
export const toSeverityColor = (severity: Severity): string => SEVERITY_COLOR_VAR[severity];

/** score → 색상. null(데이터 미수신) 이면 회색. severity 도출 없이 점수만 있는 자리에 사용. */
export const toScoreColor = (score: number | null): string =>
  SEVERITY_COLOR_VAR[scoreToSeverity(score)];

/** 숫자 표기 — null(데이터 미수신) 이면 '-'. */
export const toScoreText = (score: number | null): string =>
  score === null ? EMPTY_VALUE_TEXT : String(score);

/**
 * 카운트 메트릭 표기 — null(데이터 미수신) 이면 '-', 값이 있으면 천 단위 콤마.
 * score 와 달리 상한이 없어 자리수가 커지므로 로케일 포맷을 함께 적용한다.
 * 0 은 실측 '0건' 이므로 그대로 '0' — null 과 구분해야 한다.
 */
export const toCountText = (count: number | null): string =>
  count === null ? EMPTY_VALUE_TEXT : count.toLocaleString('en-US');
